import { io, type Socket } from 'socket.io-client'
import { useEffect, useRef, useState } from 'react'

import { applyGameStateDelta } from '@shared/games/apply-game-state-delta'
import type { GameServerEvent, GameState } from '@shared/games/protocol'
import { GAMES_SOCKET_OPTIONS, buildGamesUrl } from '@shared/lib/ws/game-socket-config'

interface Options {
  channelId: string
  enabled?: boolean
  onState: (state: GameState | null) => void
}

export function useGameSpectator({ channelId, enabled = true, onState }: Options) {
  const [active, setActive] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const localStateRef = useRef<GameState | null>(null)
  const onStateRef = useRef(onState)

  useEffect(() => {
    onStateRef.current = onState
  }, [onState])

  useEffect(() => {
    if (!enabled || !channelId) return

    localStateRef.current = null
    const socket = io(buildGamesUrl(), {
      ...GAMES_SOCKET_OPTIONS,
      query: { role: 'spectator', channelId },
    })
    socketRef.current = socket

    socket.on('connect', () => setActive(true))
    socket.on('disconnect', () => setActive(false))
    socket.on('connect_error', (err) => {
      console.warn('[game/spectator] connect_error', err.message)
    })

    socket.on('game:event', (event: GameServerEvent) => {
      if (event.type === 'state_snapshot') {
        localStateRef.current = event.state
        onStateRef.current(event.state)
        return
      }

      if (event.type === 'state_delta') {
        const prev = localStateRef.current
        if (!prev) return
        const merged = applyGameStateDelta(
          prev as unknown as Record<string, unknown>,
          event.delta,
        ) as unknown as GameState
        localStateRef.current = merged
        onStateRef.current(merged)
        return
      }

      if (event.type === 'session_ended') {
        localStateRef.current = null
        onStateRef.current(null)
      }
    })

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
      localStateRef.current = null
      setActive(false)
      onStateRef.current(null)
    }
  }, [channelId, enabled])

  return { active }
}
